import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import styles from "./DayWithVertik.module.scss";

const timelineEntries = [
  {
    time: "8:30 AM",
    title: "Start with clarity",
    description:
      "Vertik shows your top priorities, overdue follow-ups, meeting prep, and deal risks for the day.",
  },
  {
    time: "10:00 AM",
    title: "Work the right leads",
    description:
      "A new lead asks about pricing. Vertik scores it, pulls context, drafts a reply, and creates the next task.",
  },
  {
    time: "12:00 PM",
    title: "Walk into meetings prepared",
    description:
      "It summarizes the account, last conversations, open risks, stakeholders, and the questions to ask.",
  },
  {
    time: "2:00 PM",
    title: "Turn meetings into action",
    description:
      "After the call, Vertik drafts the recap, updates CRM, creates follow-ups, and flags deal-health changes.",
  },
  {
    time: "4:00 PM",
    title: "Rescue what's slipping",
    description:
      "It notices a high-value opportunity has gone quiet and recommends the next move to re-engage.",
  },
  {
    time: "6:00 PM",
    title: "Improve for tomorrow",
    description:
      "Vertik shows what got done, what slipped, and the one pattern worth improving next.",
  },
];

const DayWithVertik = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.08 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: "easeOut" },
    },
  };

  return (
    <motion.section
      id="day-with-vertik"
      ref={ref}
      className={styles.section}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className="container-custom">
        <motion.div className={styles.badge} variants={itemVariants}>
          <span className={styles.badgeDot} aria-hidden="true" />
          A Day with Vertik
        </motion.div>

        <motion.h2 className={styles.headline} variants={itemVariants}>
          From morning brief to closed deal — Vertik stays with you
        </motion.h2>

        <motion.ol className={styles.timeline} variants={itemVariants}>
          {timelineEntries.map((entry, index) => (
            <motion.li
              key={entry.time}
              className={styles.timelineItem}
              variants={itemVariants}
            >
              <div className={styles.timelineMarker} aria-hidden="true">
                <span className={styles.timelineDot} />
                {index < timelineEntries.length - 1 && (
                  <span className={styles.timelineLine} />
                )}
              </div>

              <div className={styles.timelineContent}>
                <span className={styles.timelineTime}>{entry.time}</span>
                <h3 className={styles.timelineTitle}>{entry.title}</h3>
                <p className={styles.timelineDescription}>{entry.description}</p>
              </div>
            </motion.li>
          ))}
        </motion.ol>
      </div>
    </motion.section>
  );
};

export default DayWithVertik;
